import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    try {
      const { data } = await axios.get(`${API}/audit-logs`, { withCredentials: true });
      setLogs(data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const actionColors = {
    create: 'bg-green-100 text-green-800 border-green-200',
    update: 'bg-blue-100 text-blue-800 border-blue-200',
    delete: 'bg-red-100 text-red-800 border-red-200'
  };

  const filteredLogs = filter === 'all' ? logs : logs.filter(l => l.resource_type === filter);
  const resourceTypes = [...new Set(logs.map(l => l.resource_type))];

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8" data-testid="audit-logs-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="audit-logs-title">Audit Trail</h1>
        <p className="mt-2 text-zinc-600">Complete history of all actions in the system</p>
      </div>

      <div className="mb-4 flex gap-2 flex-wrap" data-testid="audit-filters">
        <button onClick={() => setFilter('all')} className={`px-4 py-2 text-sm font-semibold border transition-colors ${filter === 'all' ? 'bg-black text-white border-black' : 'border-zinc-300 hover:border-black'}`} data-testid="filter-all">
          All ({logs.length})
        </button>
        {resourceTypes.map(type => (
          <button key={type} onClick={() => setFilter(type)} className={`px-4 py-2 text-sm font-semibold border transition-colors ${filter === type ? 'bg-black text-white border-black' : 'border-zinc-300 hover:border-black'}`} data-testid={`filter-${type}`}>
            {type.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="audit-logs-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Timestamp</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">User</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Action</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Resource</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {filteredLogs.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-12 text-center text-zinc-500" data-testid="no-logs">No audit logs found</td></tr>
              ) : filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-zinc-50 transition-colors" data-testid={`log-${log.id}`}>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                  <td className="px-6 py-4 text-zinc-900 text-sm">{log.user_email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold border ${actionColors[log.action] || 'bg-zinc-100 text-zinc-800 border-zinc-200'}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{log.resource_type}</td>
                  <td className="px-6 py-4 text-zinc-600 text-xs font-mono">
                    {log.details && Object.keys(log.details).length > 0 ? JSON.stringify(log.details).slice(0, 100) : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
